import { DEFAULT_LANG } from '@rover/shared/js/constants/i18n/language';
import inject from '@rover/utilities/injector';

const userLang = inject('Rover.context.language', DEFAULT_LANG);
// - Loosely adapted from luxon preset: https://moment.github.io/luxon/docs/manual/formatting.html
// with some exceptions: month, day, hour, minute, second always 2-digit
// - Use language instead of locale as key because of moment and luxon
// - i18n format is not being used until we switch over to luxon/Intl.DateTimeFormat
// - format that only defines en-us is not internationalized. They are used internally or cannot be internationalized
// - ADD NEW FORMAT: make sure the format is translatable to i18n format:
// https://developer.mozilla.org/en-us/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
// - USAGE with moment:
//      import DF from '@rover/shared/js/constants/i18n/datetime';
//      moment().format(DF.DATE_ISO);
const I18N_FORMAT_MAP = {
  'en-us': {
    // 2018-06-02
    DATE_ISO: 'YYYY-MM-DD',
    // June 02, 2018
    DATE_FULL: 'MMMM DD, YYYY',
    // Jun 02, 2018
    DATE_MED: 'MMM DD, YYYY',
    // 06/02/2018
    DATE_SHORT: 'MM/DD/YYYY',
    // match DATE_SHORT configure date-picker, non-moment format
    DATE_PICKER: 'mm/dd/yy',
    // Sat, 06/02/18
    DATE_WEEK_SHORT: 'ddd, MM/DD/YY',
    // June 02
    MONTH_DAY_FULL: 'MMMM DD',
    // Jun 02
    MONTH_DAY_MED: 'MMM DD',
    // 06/02
    MONTH_DAY_SHORT: 'MM/DD',
    // Saturday, June 02
    MONTH_DAY_WEEK_FULL: 'dddd, MMMM DD',
    // Sat, Jun 02
    MONTH_DAY_WEEK_MED: 'ddd, MMM DD',
    // Sat, 06/02
    MONTH_DAY_WEEK_SHORT: 'ddd, MM/DD',
    // June 2018
    MONTH_YEAR_FULL: 'MMMM YYYY',
    // Jun 2018
    MONTH_YEAR_MED: 'MMM YYYY',
    // 06/2018
    MONTH_YEAR_SHORT: 'MM/YYYY',
    // 01:30 PM
    TIME_SIMPLE: 'hh:mm A',
    // 01:30PM - match with TIME_SIMPLE. Used for time picker in PHP format
    TIME_PICKER: 'h:i A',
    // 01:30:23 PM
    TIME_WITH_SECOND: 'hh:mm:ss A',
    // 13:30
    TIME_24_SIMPLE: 'HH:mm',
    // 13:30:23
    TIME_24_WITH_SECOND: 'HH:mm:ss',
  },
  i18n: {
    // June 02, 2018
    DATE_FULL: { year: 'numeric', month: 'long', day: '2-digit' },
    // Jun 02, 2018
    DATE_MED: { year: 'numeric', month: 'short', day: '2-digit' },
    // 06/02/2018
    DATE_SHORT: { year: 'numeric', month: '2-digit', day: '2-digit' },
    // Sat, 06/02/18
    DATE_WEEK_SHORT: {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
    },
    // June 02
    MONTH_DAY_FULL: { month: 'long', day: '2-digit' },
    // Jun 02
    MONTH_DAY_MED: { month: 'short', day: '2-digit' },
    // 06/02
    MONTH_DAY_SHORT: { month: '2-digit', day: '2-digit' },
    // Saturday, June 02
    MONTH_DAY_WEEK_FULL: { month: 'long', day: '2-digit', weekday: 'long' },
    // Sat, Jun 02
    MONTH_DAY_WEEK_MED: { month: 'short', day: '2-digit', weekday: 'short' },
    // Sat, 06/02
    MONTH_DAY_WEEK_SHORT: {
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
    },
    // June 2018
    MONTH_YEAR_FULL: { year: 'numeric', month: 'long' },
    // Jun 2018
    MONTH_YEAR_MED: { year: 'numeric', month: 'short' },
    // 06/2018
    MONTH_YEAR_SHORT: { year: 'numeric', month: '2-digit' },
    // 01:30 PM - 12hr or 24hr dependent on locale
    TIME_SIMPLE: { hour: '2-digit', minute: '2-digit' },
    // 01:30:23 PM - 12hr or 24hr dependent on locale
    TIME_WITH_SECOND: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
    // 13:30
    TIME_24_SIMPLE: { hour: '2-digit', minute: '2-digit', hour12: false },
    // 13:30:23
    TIME_24_WITH_SECOND: {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    },
  },
  'en-gb': {
    // 02 June 2018
    DATE_FULL: 'DD MMMM YYYY',
    // 02 Jun 2018
    DATE_MED: 'DD MMM YYYY',
    // 02/06/2018
    DATE_SHORT: 'DD/MM/YYYY',
    // match DATE_SHORT configure date-picker, non-moment format
    DATE_PICKER: 'dd/mm/yy',
    // Sat, 02/06/18
    DATE_WEEK_SHORT: 'ddd, DD/MM/YY',
    // 02 June
    MONTH_DAY_FULL: 'DD MMMM',
    // 02 Jun
    MONTH_DAY_MED: 'DD MMM',
    // 02/06
    MONTH_DAY_SHORT: 'DD/MM',
    // Saturday 02 June
    MONTH_DAY_WEEK_FULL: 'dddd DD MMMM',
    // Sat 02 Jun
    MONTH_DAY_WEEK_MED: 'ddd DD MMM',
    // Sat 02/06
    MONTH_DAY_WEEK_SHORT: 'ddd DD/MM',
    // June 2018
    MONTH_YEAR_FULL: 'MMMM YYYY',
    // Jun 2018
    MONTH_YEAR_MED: 'MMM YYYY',
    // 06/2018
    MONTH_YEAR_SHORT: 'MM/YYYY',
    // 13:30
    TIME_SIMPLE: 'HH:mm',
    // 13:30 - match with TIME_SIMPLE. Used for time picker in PHP format
    TIME_PICKER: 'H:i',
    // 13:30:23
    TIME_WITH_SECOND: 'HH:mm:ss',
    // 13:30
    TIME_24_SIMPLE: 'HH:mm',
    // 13:30:23
    TIME_24_WITH_SECOND: 'HH:mm:ss',
  },
};

export const mf = (format, lang = userLang) => {
  // function that return the format based on user lang. The default lang is 'en-us'
  // cannot live in util because it will break some test due to circular dependency(but not in runtime)
  // if lang does not exist or format does not exist in current lang then fallback to default language
  const langs = I18N_FORMAT_MAP[lang] || I18N_FORMAT_MAP[DEFAULT_LANG];
  return langs[format] || I18N_FORMAT_MAP[DEFAULT_LANG][format] || format;
};

const EXTERNAL_FORMAT_MAP = {
  'M/DD': mf('MONTH_DAY_SHORT'),
};

// Right now, this keys are DATE_FULL = 'DD MMMM YYYY' (format in user's current locale).
// See note at the top for usage
// With luxon or intl api, we will simplify these constant to DATE_FULL = { year: 'numeric', ... }
// and none of the user's code has to change
const FORMAT_KEYS = Object.keys(I18N_FORMAT_MAP[DEFAULT_LANG]).reduce(
  (acc, key) => {
    acc[key] = mf(key);
    return acc;
  },
  { ...EXTERNAL_FORMAT_MAP }
);

export { FORMAT_KEYS as default, I18N_FORMAT_MAP, EXTERNAL_FORMAT_MAP };



// WEBPACK FOOTER //
// ../shared/js/constants/i18n/datetime.js